import role17Schema from '../../../../assets/forms/role-510000.form.json';
import { SSMT, SSMT_SCOPE } from '../../../constants/qnames';

/**
 * Shared business rules for Role 17 (Statement of Cash Flows — Direct Method).
 *
 * Role 17 is the direct-method sibling of role 18 (indirect method). Where role
 * 18 reconciles profit to operating cash flows via non-cash adjustments, role
 * 17 presents gross cash receipts and payments directly. The two sheets are
 * alternative presentations of the same economic information — a filing uses
 * one OR the other, never both, so there is no cross-sheet equality tying
 * role 17 to role 18 today.
 *
 * Single-variant schema: `role-510000.form.json` ships ONE file (no
 * MFRS/MPERS split) because the `ssmt:` qname prefix carries through both
 * accounting standards. Compare this with role 12 / 14 where the SOPL layout
 * branches on standard. Role 17's `calculateTotals()` therefore needs no
 * standard-dependent prefix selection.
 *
 * Both role17-510000-statement-of-cash-flows-direct.component.ts AND
 * `role17CashFlowsDirectValidator` import from this file so the column-suffix
 * ladder, calculation groups, and LB-001 fix-guard helpers live in exactly
 * one place.
 *
 * ---- Sheet-status posture (mirror of roles 11 / 14) ---------------------
 *
 * The component does NOT call
 * `stateService.updateSheetLevelStatus('statementOfCashFlowsDirectMethod', ...)`
 * anywhere. The only sheet-status write is
 * `markSheetInitializedWithStatus('statementOfCashFlowsDirectMethod', false)`
 * in `loadSchema()` after a 600 ms settle timeout. There is no
 * `evaluateSheetStatus()` loop. `buildForm()` passes an empty `validators:
 * ValidatorFn[] = []` array into every `new FormControl(...)` so the form's
 * `invalid` bit can never flip through schema-derived rules, and the schema
 * itself declares ZERO `required: true` fields, ZERO `validators` entries,
 * and ZERO `validation.equality` blocks. Net effect: the editor's published
 * `hasErrors` for `statementOfCashFlowsDirectMethod` is deterministically
 * `false` today.
 *
 * The pure validator returns `hasErrors: false` deterministically but carries
 * the LB-001 fix-guard pattern (required-field detection from BOTH
 * `control.required === true` AND unconditional `validators: [{ name:
 * 'required' }]` entries, `conditionalOn === undefined`) so any future schema
 * edit that adds a required cell is picked up automatically — matching the
 * guard carried by roles 22 / 21 / 13 / 9 / 14 / 11.
 *
 * ---- Calculations --------------------------------------------------------
 *
 * The component's `calculateTotals()` derives six calculated rows per visible
 * column suffix (see `ROLE17_CALCULATION_GROUPS` below). Each group is
 * computed from sibling input cells on the SAME row set — no cross-sheet
 * inputs, no dimension crossings, no accounting-standard branching. The
 * groups are a row-wise arithmetic formula per suffix:
 *
 *   CashGeneratedFromOperations
 *     = CashReceiptsFromCustomers - CashPaidToSuppliersAndEmployees
 *
 *   NetCashFromOperatingActivities
 *     = CashGeneratedFromOperations
 *     - InterestPaid
 *     - IncomeTaxesPaid
 *     + InterestReceived
 *     + DividendsReceived
 *
 *   NetCashUsedInInvestingActivities
 *     = - PurchaseOfPropertyPlantAndEquipment
 *       + ProceedsFromDisposalOfPropertyPlantAndEquipment
 *       - PurchaseOfIntangibleAssets
 *       - PurchaseOfInvestments
 *       + ProceedsFromDisposalOfInvestments
 *       - AcquisitionOfSubsidiaries
 *
 *   NetCashFromFinancingActivities
 *     = ProceedsFromIssuanceOfShareCapital
 *     + ProceedsFromBorrowings
 *     - RepaymentOfBorrowings
 *     - PaymentOfLeaseLiabilities
 *     - DividendsPaid
 *     - DividendsPaidToNonControllingInterests
 *
 *   NetIncreaseDecreaseInCashAndCashEquivalents
 *     = NetCashFromOperatingActivities
 *     + NetCashUsedInInvestingActivities
 *     + NetCashFromFinancingActivities
 *
 *   CashAndCashEquivalentsAtEndOfYear
 *     = CashAndCashEquivalentsAtBeginningOfYear
 *     + NetIncreaseDecreaseInCashAndCashEquivalents
 *     + EffectOfExchangeRateChanges
 *
 * Calculated cells are persisted back into the
 * `statementOfCashFlowsDirectMethod` payload via
 * `form.get(...)?.setValue(..., { emitEvent: false })`, so the pure validator
 * reads post-calculation values directly from `sheetData` (same Tier 4 Batch
 * 1 parity stance — read persisted post-calc values, don't replay
 * arithmetic). The groups are exported so a future equality block on any of
 * these calculated totals plugs in without duplicating the input list, and
 * so the component's `calculateTotals()` refactor consumes exactly one
 * source of truth.
 *
 * ---- T-010 sweep (LB-015 parallel) --------------------------------------
 *
 * **Checked explicitly.** Both the component's `calculateTotals()` and its
 * `registerCalculatedKeys()` reference `ssmt:`-prefixed qnames; the schema
 * also ships `ssmt:`-prefixed qnames. Every qname referenced by the
 * arithmetic DOES exist in the schema — no LB-015 divergence. Keeping this
 * table as the single source of truth means any future schema-prefix
 * rename (e.g. to `ssmt-mfrs:` / `ssmt-mpers:`) fails loudly here rather
 * than silently returning zeros.
 *
 * ---- Cross-sheet posture (Tier 4) ---------------------------------------
 *
 * Per `SheetDependencyService`, `scopeOfFiling` lists
 * `statementOfCashFlowsDirectMethod` as a downstream sheet (column-visibility
 * driver — consolidation toggle + comparative-year toggle). Role 17 itself is
 * NOT a key in the dependency map, so no other sheet's cached status gets
 * invalidated when role 17 is submitted. This asymmetry is deliberate: the
 * schema declares ZERO `validation.equality` blocks, so role 17 is a pure
 * sink in the cross-sheet graph today.
 *
 * The component's `setupLayoutReactivity()` watches
 * `slicesOfInterest: ['filingPrerequisites', 'scopeOfFiling']` for layout
 * flag changes (same reload-prompt pattern as roles 8 / 10 / 12 / 15) and
 * the `modifiedState$` re-validation subscription debounces on
 * `{ r0: state.filingPrerequisites, r2: state.scopeOfFiling }`. The pure
 * validator reads `scopeOfFiling` defensively so the orchestrator's
 * cross-sheet bundle wiring tracks the same prerequisites without gold-
 * plating dependencies role 17 does not actually need today.
 *
 * Tolerated absence: when `scopeOfFiling` is missing from the bundle (parity-
 * side mid-init, or a listing/dashboard cache-miss path), the validator
 * defaults to `isConsolidated=false, hasPreviousYear=false` — the single-
 * Current-column scenario. The component's pre-mount initialisers match
 * (both flags start `false` before `checkConsolidatedStatus()` runs).
 */

/**
 * scopeOfFiling field whose value drives the Group / Company column
 * visibility. Mirrors the component's `checkConsolidatedStatus()` exactly.
 */
export const ROLE17_CONSOLIDATED_FIELD =
  SSMT_SCOPE.DisclosureOfWhetherFinancialStatementsAreConsolidated;
export const ROLE17_CONSOLIDATED_VALUE = 'Yes';

/**
 * scopeOfFiling field whose value drives the Previous-Year column
 * visibility. Mirrors the component's `checkConsolidatedStatus()` exactly
 * (both accepted string values — "Comparative information presented" and
 * "Restated comparative information" — activate the previous-year column).
 */
export const ROLE17_COMPARATIVE_FIELD =
  SSMT_SCOPE.DisclosureOfComparativeInformation;
export const ROLE17_COMPARATIVE_PRESENTED_VALUES: ReadonlyArray<string> = [
  'Comparative information presented',
  'Restated comparative information',
];

/**
 * Narrow schema-control shape. The full JSON carries more metadata
 * (ui.placeholder, ui.currency, ui.level, etc.) but the validator only needs
 * qname, type, required, validators, and the calculated/readonly flags —
 * keeping the type tight means unrelated schema additions never ripple here.
 */
export interface Role17SchemaValidator {
  name?: string;
  value?: number;
  conditionalOn?: unknown;
}

export interface Role17SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Role17SchemaValidator[];
  ui?: {
    control?: string;
    calculated?: boolean;
    readonly?: boolean;
    dualColumn?: boolean;
    level?: number;
    currency?: string;
    placeholder?: string;
    note?: string;
  };
  validation?: {
    equality?: ReadonlyArray<{
      sheet: string;
      field: string;
      operation?: 'sum' | string;
      tolerance?: number;
      message?: string;
    }>;
  };
}

export interface Role17Schema {
  role: string;
  sheetId: string;
  label: string;
  description?: string;
  controls: Role17SchemaControl[];
}

/**
 * Resolve the role 17 schema. Single-variant (no accounting-standard branch).
 * Exposed as a function for symmetry with role-9 / role-13 / role-14 pickers
 * and so a future MFRS/MPERS split (if it ever happens) only changes this
 * one place.
 */
export function getRole17Schema(): Role17Schema {
  return role17Schema as unknown as Role17Schema;
}

/**
 * Schema controls that participate in validation. Excludes section headers
 * exactly as the component's `buildForm()` does (it skips `ui.control ===
 * 'header'`). Headers never become FormControls and therefore cannot fail
 * validation.
 */
export function getRole17ValidatingControls(
  schema: Role17Schema
): ReadonlyArray<Role17SchemaControl> {
  return schema.controls.filter((control) => control.ui?.control !== 'header');
}

/**
 * Is this control a calculated / readonly total? Calculated cells are
 * populated by the component's `calculateTotals()` after valueChanges settle
 * and are never user-editable, so even if a future schema edit marked one
 * required, the validator should not flag emptiness until the calculation
 * engine has run. Mirrors the component's `isCalculated()` predicate and the
 * `disabled: !!control.ui.calculated` flag in `buildForm()`.
 */
export function isRole17CalculatedControl(
  control: Role17SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/** Empty-value check matching the component's null/undefined/'' guards. */
export function isRole17Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * LB-001 fix-guard: a control is statically required when either
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry (no `conditionalOn`). Mirrors the role-6
 * post-fix check so any future schema edit to role 17 (none exist today)
 * is picked up automatically without a separate component change.
 */
export function isRole17StaticallyRequired(
  control: Role17SchemaControl
): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some(
        (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
      )
    : false;
}

/**
 * Visible column suffixes for the current filing. Mirrors exactly the branch
 * ladder in the component's `buildForm()`, `calculateTotals()`,
 * `validateAgainstApiData()`, `getRowValidationStatus()`,
 * `getRowErrorDescription()`, and `autoFillDemo()` — the component has SIX
 * copies of this ladder today. Single source of truth here.
 *
 *   isConsolidated && hasPreviousYear  →
 *     ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious']
 *   isConsolidated && !hasPreviousYear →
 *     ['GroupCurrent', 'CompanyCurrent']
 *   !isConsolidated && hasPreviousYear →
 *     ['Current', 'Previous']
 *   !isConsolidated && !hasPreviousYear →
 *     ['Current']
 */
export type Role17ColumnSuffix =
  | 'GroupCurrent'
  | 'GroupPrevious'
  | 'CompanyCurrent'
  | 'CompanyPrevious'
  | 'Current'
  | 'Previous';

export function resolveRole17ColumnSuffixes(
  isConsolidated: boolean,
  hasPreviousYear: boolean
): ReadonlyArray<Role17ColumnSuffix> {
  if (isConsolidated && hasPreviousYear) {
    return ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
  }
  if (isConsolidated && !hasPreviousYear) {
    return ['GroupCurrent', 'CompanyCurrent'];
  }
  if (!isConsolidated && hasPreviousYear) {
    return ['Current', 'Previous'];
  }
  return ['Current'];
}

/**
 * Resolve `(isConsolidated, hasPreviousYear)` from a `scopeOfFiling` payload.
 * Mirrors the component's `checkConsolidatedStatus()` exactly so the
 * validator's column-visibility decisions track the editor bit-for-bit.
 *
 * Missing slice (parity-side mid-init, or a conversion the user has not
 * opened): defaults to the conservative "neither flag set" (single Current
 * column). The component's pre-mount state defaults the same way — both
 * flags initialise `false` before `checkConsolidatedStatus()` runs — so
 * this matches the editor's pre-mount behaviour.
 */
export function resolveRole17LayoutFlags(
  scopeOfFilingSlice: Record<string, unknown> | undefined
): { isConsolidated: boolean; hasPreviousYear: boolean } {
  if (!scopeOfFilingSlice) {
    return { isConsolidated: false, hasPreviousYear: false };
  }
  const isConsolidated =
    scopeOfFilingSlice[ROLE17_CONSOLIDATED_FIELD] === ROLE17_CONSOLIDATED_VALUE;
  const comparative = scopeOfFilingSlice[ROLE17_COMPARATIVE_FIELD];
  const hasPreviousYear =
    typeof comparative === 'string' &&
    ROLE17_COMPARATIVE_PRESENTED_VALUES.includes(comparative);
  return { isConsolidated, hasPreviousYear };
}

/**
 * Build the flat key the editor stores its column values under
 * (`qname + suffix`). Used by the validator for sheetData reads and by the
 * component when it calls into shared calculation helpers.
 */
export function buildRole17FlatKey(
  qname: string,
  suffix: Role17ColumnSuffix
): string {
  return `${qname}${suffix}`;
}

/**
 * Calculation-group row shape. Each entry describes one calculated total
 * and its contributing inputs with their sign. Modelled as an explicit
 * `{ qname, sign }` tuple instead of `subtractFromIndex` (role 11 style)
 * because role 17 has multiple negative terms per group and a positional
 * index would be misleading.
 */
export interface Role17CalculationTerm {
  qname: string;
  sign: 1 | -1;
}

export interface Role17CalculationGroup {
  /** qname of the calculated total cell. */
  total: string;
  /** Contributing input terms with their sign. Sum(term.sign * value). */
  inputs: ReadonlyArray<Role17CalculationTerm>;
}

/**
 * Calculation groups — single source of truth for the six row-level totals
 * the editor's `calculateTotals()` derives, in evaluation order. Each group
 * is evaluated per visible column suffix; the running totals from earlier
 * groups feed later ones (NetIncreaseDecrease pulls in the three Net*
 * sub-totals, and CashAtEndOfYear pulls in NetIncreaseDecrease).
 *
 * **Dependencies are implicit in the order**: when we evaluate
 * NetIncreaseDecreaseInCashAndCashEquivalents the three
 * NetCash*Activities cells have already been written back to the form by
 * earlier iterations of the loop. The component relies on the same ordering.
 *
 * Exported so both the validator and the component's `calculateTotals()`
 * consume the same list. Today the validator does NOT use this table (no
 * equality blocks, no required cells), but:
 *   • The six `total` qnames are also returned by
 *     `getRole17CalculatedQnames()` so the component's
 *     `registerCalculatedKeys()` stays DRY.
 *   • A future equality block on any calculated total plugs in without
 *     re-deriving the formula.
 *   • If the LB-015 pattern surfaces later (qname prefix rename), the
 *     mismatch fails at one place rather than silently returning zeros.
 */
export const ROLE17_CALCULATION_GROUPS: ReadonlyArray<Role17CalculationGroup> = [
  {
    total: SSMT.CashGeneratedFromOperations,
    inputs: [
      { qname: SSMT.CashReceiptsFromCustomers, sign: 1 },
      { qname: SSMT.CashPaidToSuppliersAndEmployees, sign: -1 },
    ],
  },
  {
    total: SSMT.NetCashFromOperatingActivities,
    inputs: [
      { qname: SSMT.CashGeneratedFromOperations, sign: 1 },
      { qname: SSMT.InterestPaid, sign: -1 },
      { qname: SSMT.IncomeTaxesPaid, sign: -1 },
      { qname: SSMT.InterestReceived, sign: 1 },
      { qname: SSMT.DividendsReceived, sign: 1 },
    ],
  },
  {
    total: SSMT.NetCashUsedInInvestingActivities,
    inputs: [
      { qname: SSMT.PurchaseOfPropertyPlantAndEquipment, sign: -1 },
      { qname: SSMT.ProceedsFromDisposalOfPropertyPlantAndEquipment, sign: 1 },
      { qname: SSMT.PurchaseOfIntangibleAssets, sign: -1 },
      { qname: SSMT.PurchaseOfInvestments, sign: -1 },
      { qname: SSMT.ProceedsFromDisposalOfInvestments, sign: 1 },
      { qname: SSMT.AcquisitionOfSubsidiaries, sign: -1 },
    ],
  },
  {
    total: SSMT.NetCashFromFinancingActivities,
    inputs: [
      { qname: SSMT.ProceedsFromIssuanceOfShareCapital, sign: 1 },
      { qname: SSMT.ProceedsFromBorrowings, sign: 1 },
      { qname: SSMT.RepaymentOfBorrowings, sign: -1 },
      { qname: SSMT.PaymentOfLeaseLiabilities, sign: -1 },
      { qname: SSMT.DividendsPaid, sign: -1 },
      { qname: SSMT.DividendsPaidToNonControllingInterests, sign: -1 },
    ],
  },
  {
    total: SSMT.NetIncreaseDecreaseInCashAndCashEquivalents,
    inputs: [
      { qname: SSMT.NetCashFromOperatingActivities, sign: 1 },
      { qname: SSMT.NetCashUsedInInvestingActivities, sign: 1 },
      { qname: SSMT.NetCashFromFinancingActivities, sign: 1 },
    ],
  },
  {
    total: SSMT.CashAndCashEquivalentsAtEndOfYear,
    inputs: [
      { qname: SSMT.CashAndCashEquivalentsAtBeginningOfYear, sign: 1 },
      { qname: SSMT.NetIncreaseDecreaseInCashAndCashEquivalents, sign: 1 },
      { qname: SSMT.EffectOfExchangeRateChanges, sign: 1 },
    ],
  },
];

/**
 * Flattened list of every calculated total qname the component's
 * `calculateTotals()` writes. Returned as a plain string[] so the component's
 * `registerCalculatedKeys()` can call
 * `calculatedQnames.flatMap(q => suffixes.map(s => q + s))` unchanged.
 */
export function getRole17CalculatedQnames(): ReadonlyArray<string> {
  return ROLE17_CALCULATION_GROUPS.map((group) => group.total);
}
